/**
 * Sync Service - Delta-Updates und Daten-Synchronisation
 * 
 * Verantwortlich für:
 * - Full-Sync (manuell in Settings)
 * - Delta-Updates (automatisch bei guter Verbindung)
 * - Bandwidth-Detection mit Network Information API
 * - Background Sync für Bestellungen
 */

import { openDB } from 'idb';
import { canPrefetchNow, canLoadRemotePartImages, prefetchCatalogImages } from './imageCache';
import {
  DB_NAME, DB_VERSION,
  saveDocumentationForOffline, saveDocAttachmentFile,
  saveNewsFeeds, saveNewsFeedAttachments,
  saveEasyhelpCases, saveEasyhelpMessages, saveEasyhelpFileCache, getEasyhelpFileCache,
  saveNewsFeedAttachmentFile, getNewsFeedAttachmentFileOffline,
  saveCustomers, savePasswords
} from './dbService.js';

// API-Basis-URL (in Production aus ENV)
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const IS_DEV = import.meta.env.DEV;

// Bandwidth-Schwellenwerte
const BANDWIDTH_THRESHOLDS = {
  MIN_MBPS: 1.5,              // Minimum für Delta-Update
  OPTIMAL_MBPS: 5,            // Optimal für große Updates
  MAX_SIZE_3G: 5 * 1024 * 1024, // 5 MB bei 3G
};

// ============================================================
// NETWORK INFORMATION API - Bandwidth Check
// ============================================================

/**
 * Prüft, ob die Netzwerkverbindung für Sync geeignet ist
 * 
 * @returns {Object} { allowed, reason, limitSize }
 */
export function isSuitableForSync() {
  // Ersatzteildaten nur im WLAN (bzw. laut Einstellung "Offline-Daten laden")
  if (!canPrefetchNow()) {
    return {
      allowed: false,
      reason: 'Mobilfunk/Datensparen – Ersatzteildaten werden nur im WLAN geladen',
      limitSize: null
    };
  }

  // Network Information API verfügbar?
  const connection = navigator.connection || 
                    navigator.mozConnection || 
                    navigator.webkitConnection;

  if (!connection) {
    console.warn('[Sync] Network Information API nicht verfügbar');
    return {
      allowed: true,
      reason: 'Network API nicht verfügbar - erlaube Sync',
      limitSize: null
    };
  }

  // User hat Datensparmodus aktiviert?
  if (connection.saveData) {
    return {
      allowed: false,
      reason: 'Datensparmodus aktiv',
      limitSize: null
    };
  }

  // Effektive Verbindungsgeschwindigkeit
  const effectiveType = connection.effectiveType; // 'slow-2g', '2g', '3g', '4g'
  const downlink = connection.downlink; // Mbit/s (estimated)

  console.log(`[Sync] Connection: ${effectiveType}, Downlink: ${downlink} Mbit/s`);

  // Zu langsame Verbindung
  if (effectiveType === 'slow-2g' || effectiveType === '2g') {
    return {
      allowed: false,
      reason: `Verbindung zu langsam (${effectiveType})`,
      limitSize: null
    };
  }

  // Downlink unter Schwellenwert
  if (downlink < BANDWIDTH_THRESHOLDS.MIN_MBPS) {
    return {
      allowed: false,
      reason: `Bandbreite zu niedrig (${downlink.toFixed(1)} Mbit/s)`,
      limitSize: null
    };
  }

  // 3G: Beschränkte Downloads
  if (effectiveType === '3g') {
    const isWiFi = connection.type === 'wifi';
    
    if (!isWiFi) {
      return {
        allowed: true,
        reason: '3G Mobilfunk - begrenzte Updates',
        limitSize: BANDWIDTH_THRESHOLDS.MAX_SIZE_3G
      };
    }
  }

  // Optimale Verbindung (4G oder WiFi)
  return {
    allowed: true,
    reason: `Gute Verbindung (${effectiveType}, ${downlink.toFixed(1)} Mbit/s)`,
    limitSize: null
  };
}

/**
 * Event-Listener für Netzwerkänderungen
 */
export function setupNetworkMonitoring(onOnline, onOffline) {
  window.addEventListener('online', () => {
    console.log('[Sync] Network online');
    if (onOnline) onOnline();
  });

  window.addEventListener('offline', () => {
    console.log('[Sync] Network offline');
    if (onOffline) onOffline();
  });

  // Connection-Change Events (Network Information API)
  const connection = navigator.connection || 
                    navigator.mozConnection || 
                    navigator.webkitConnection;

  if (connection) {
    connection.addEventListener('change', () => {
      console.log('[Sync] Connection changed:', connection.effectiveType);
      
      // Bei Verbesserung der Verbindung: Delta-Check triggern
      if (connection.effectiveType === '4g' || connection.type === 'wifi') {
        checkAndTriggerDeltaUpdate();
      }
    });
  }
}

// ============================================================
// DELTA-UPDATE - Automatisch bei guter Verbindung
// ============================================================

/**
 * Prüft, ob Delta-Update möglich und nötig ist
 */
export async function checkAndTriggerDeltaUpdate() {
  try {
    // Sync-Status aus DB laden
    const db = await openDB(DB_NAME, DB_VERSION);
    const syncInProgress = await db.get('sync_metadata', 'sync_in_progress');

    if (syncInProgress?.value) {
      console.log('[Sync] Sync bereits läuft');
      return { success: false, reason: 'Sync läuft bereits' };
    }

    // Network-Check
    const networkCheck = isSuitableForSync();
    if (!networkCheck.allowed) {
      console.log('[Sync] Network nicht geeignet:', networkCheck.reason);
      return { success: false, reason: networkCheck.reason };
    }

    // Letzter Sync-Timestamp
    const lastSync = await db.get('sync_metadata', 'last_delta_sync');
    const lastSyncTime = lastSync?.value ? new Date(lastSync.value) : null;

    // Mindestens 1 Stunde zwischen Auto-Syncs
    if (lastSyncTime) {
      const hoursSinceSync = (Date.now() - lastSyncTime.getTime()) / (1000 * 60 * 60);
      if (hoursSinceSync < 1) {
        console.log('[Sync] Zu früh für nächsten Auto-Sync');
        return { success: false, reason: 'Zu früh für nächsten Sync' };
      }
    }

    // Delta-Update durchführen
    console.log('[Sync] Triggering delta update...');
    return await performDeltaUpdate(networkCheck.limitSize);

  } catch (error) {
    console.error('[Sync] Check failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Führt Delta-Update durch
 * 
 * @param {number|null} maxSize - Max Download-Größe (Bytes) oder null
 */
export async function performDeltaUpdate(maxSize = null) {
  const db = await openDB(DB_NAME, DB_VERSION);

  try {
    // Sync-Flag setzen
    await db.put('sync_metadata', {
      key: 'sync_in_progress',
      value: true,
      updatedAt: new Date().toISOString()
    });

    // Letzter Sync-Timestamp
    const lastSync = await db.get('sync_metadata', 'last_full_sync') || 
                     await db.get('sync_metadata', 'last_delta_sync');
    
    const since = lastSync?.value || new Date(0).toISOString();

    console.log(`[Sync] Requesting delta since: ${since}`);

    // API-Call: Delta-Änderungen abrufen
    const response = await fetch(`${API_BASE_URL}/sync/delta?since=${since}`, {
      headers: {
        'Authorization': `Bearer ${await getSessionToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    console.log('[Sync] Delta response:', {
      added: data.added?.length || 0,
      updated: data.updated?.length || 0,
      deleted: data.deleted?.length || 0,
      totalSize: data.totalSize
    });

    // Size-Check
    if (maxSize && data.totalSize > maxSize) {
      console.warn(`[Sync] Update zu groß: ${data.totalSize} > ${maxSize} bytes`);
      return {
        success: false,
        reason: `Update zu groß (${(data.totalSize / 1024 / 1024).toFixed(1)} MB)`,
        requiresManualSync: true
      };
    }

    // Änderungen anwenden
    const result = await applyDeltaChanges(db, data);

    // Sync-Timestamp aktualisieren
    await db.put('sync_metadata', {
      key: 'last_delta_sync',
      value: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // Event auslösen, damit UI aktualisiert wird
    window.dispatchEvent(new CustomEvent('sync-completed', { 
      detail: { 
        type: 'delta',
        added: result.addedCount,
        updated: result.updatedCount,
        deleted: result.deletedCount
      } 
    }));

    return {
      success: true,
      ...result
    };

  } catch (error) {
    console.error('[Sync] Delta update failed:', error);
    return {
      success: false,
      error: error.message
    };
  } finally {
    // Sync-Flag zurücksetzen
    await db.put('sync_metadata', {
      key: 'sync_in_progress',
      value: false,
      updatedAt: new Date().toISOString()
    });
  }
}

/**
 * Wendet Delta-Änderungen auf die lokale DB an
 */
async function applyDeltaChanges(db, deltaData) {
  const { added = [], updated = [], deleted = [] } = deltaData;

  const tx = db.transaction(['parts', 'categories', 'devices', 'customers'], 'readwrite');
  const partsStore = tx.objectStore('parts');
  const categoriesStore = tx.objectStore('categories');
  const devicesStore = tx.objectStore('devices');
  const customersStore = tx.objectStore('customers');

  // Neue/Update Teile
  for (const part of [...added, ...updated]) {
    await partsStore.put(part);
  }

  // Gelöschte Teile
  for (const deletedId of deleted) {
    await partsStore.delete(deletedId);
  }

  // Kategorien (falls vorhanden)
  if (deltaData.categories) {
    for (const category of deltaData.categories) {
      await categoriesStore.put(category);
    }
  }

  // Devices (falls vorhanden)
  if (deltaData.devices) {
    for (const device of deltaData.devices) {
      await devicesStore.put(device);
    }
  }

  // Customers (falls vorhanden)
  if (deltaData.customers) {
    for (const customer of deltaData.customers) {
      await customersStore.put(customer);
    }
  }

  // Passwords (falls vorhanden)
  if (deltaData.passwords) {
    await savePasswords(deltaData.passwords);
  }

  await tx.done;

  // Teil-Anzahl aktualisieren (neue Transaktion nach tx.done)
  const countTx = db.transaction(['parts'], 'readonly');
  const totalCount = await countTx.objectStore('parts').count();
  await countTx.done;

  await db.put('sync_metadata', {
    key: 'total_parts_count',
    value: totalCount,
    updatedAt: new Date().toISOString()
  });

  // Bilder im Hintergrund laden (nach DB-Operationen)
  await downloadDeltaImages(added, updated);

  return {
    addedCount: added.length,
    updatedCount: updated.length,
    deletedCount: deleted.length,
    totalParts: totalCount
  };
}

/**
 * Lädt Bilder für neue/aktualisierte Teile
 * - Läuft im Hintergrund
 * - Kleine Batches um Netzwerk nicht zu überlasten
 * - Sendet Fortschritts-Events
 */
async function downloadDeltaImages(addedParts, updatedParts) {
  // Nur neue/geänderte Teile – unveränderte Bilder liegen bereits im Cache (Version = Dateiname)
  if (IS_DEV) return;
  const token = await getSessionToken();
  const parts = [...(addedParts || []), ...(updatedParts || [])];
  const result = await prefetchCatalogImages({ parts }, API_BASE_URL, token);
  console.log('[Sync] Bilder (Delta):', result);
}

// ============================================================
// FULL-SYNC - Manuell aus Settings
// ============================================================

/**
 * Führt vollständigen Sync durch (alle Daten neu laden)
 * 
 * @param {Function} onProgress - Callback für Fortschritt (0-100)
 */
export async function performFullSync(onProgress = null) {
  const db = await openDB(DB_NAME, DB_VERSION);

  try {
    // Sync-Flag
    await db.put('sync_metadata', {
      key: 'sync_in_progress',
      value: true,
      updatedAt: new Date().toISOString()
    });

    if (onProgress) onProgress(0, 'Verbindung wird hergestellt...');

    // Schritt 1: Kategorien laden (5%)
    console.log('[Sync] Fetching categories...');
    const categories = await fetchWithAuth(`${API_BASE_URL}/api/categories`);
    
    const categoriesTx = db.transaction('categories', 'readwrite');
    await categoriesTx.objectStore('categories').clear();
    for (const cat of categories) {
      await categoriesTx.objectStore('categories').put(cat);
    }
    await categoriesTx.done;

    if (onProgress) onProgress(5, 'Kategorien geladen');

    // Schritt 2: Teile-Metadaten laden (20%)
    console.log('[Sync] Fetching parts metadata...');
    const parts = await fetchWithAuth(`${API_BASE_URL}/api/parts`);
    
    const partsTx = db.transaction('parts', 'readwrite');
    await partsTx.objectStore('parts').clear();
    for (const part of parts) {
      await partsTx.objectStore('parts').put(part);
    }
    await partsTx.done;

    if (onProgress) onProgress(20, `${parts.length} Ersatzteile geladen`);

    // Schritt 3: Bilder laden (20% - 95%)
    console.log('[Sync] Downloading images...');
    
    const BATCH_SIZE = 10;
    let loadedImages = 0;

    for (let i = 0; i < parts.length; i += BATCH_SIZE) {
      const batch = parts.slice(i, i + BATCH_SIZE);
      
      await Promise.all(
        batch.map(async (part) => {
          await downloadPartImages(part);
        })
      );

      loadedImages += batch.length;
      const progress = 20 + Math.floor((loadedImages / parts.length) * 75);
      
      if (onProgress) {
        onProgress(progress, `Bilder laden... ${loadedImages}/${parts.length}`);
      }
    }

    // Schritt 4: Metadaten aktualisieren (100%)
    await db.put('sync_metadata', {
      key: 'last_full_sync',
      value: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    await db.put('sync_metadata', {
      key: 'total_parts_count',
      value: parts.length,
      updatedAt: new Date().toISOString()
    });

    if (onProgress) onProgress(100, 'Synchronisation abgeschlossen');

    console.log('[Sync] Full sync completed successfully');

    // Event auslösen, damit UI aktualisiert wird
    window.dispatchEvent(new CustomEvent('sync-completed', { 
      detail: { 
        type: 'full',
        categoriesCount: categories.length,
        partsCount: parts.length
      } 
    }));

    return {
      success: true,
      categoriesCount: categories.length,
      partsCount: parts.length
    };

  } catch (error) {
    console.error('[Sync] Full sync failed:', error);
    
    if (onProgress) onProgress(-1, `Fehler: ${error.message}`);

    return {
      success: false,
      error: error.message
    };
  } finally {
    // Sync-Flag zurücksetzen
    await db.put('sync_metadata', {
      key: 'sync_in_progress',
      value: false,
      updatedAt: new Date().toISOString()
    });
  }
}

/**
 * Lädt Bilder für ein Teil
 */
async function downloadPartImages(part) {
  if (IS_DEV) return;
  const token = await getSessionToken();
  await prefetchCatalogImages({ parts: [part] }, API_BASE_URL, token);
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Fetch mit Authentication
 */
async function fetchWithAuth(url) {
  const token = await getSessionToken();
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Holt Session-Token aus IndexedDB
 */
async function getSessionToken() {
  const db = await openDB(DB_NAME, DB_VERSION);
  const tokenEntry = await db.get('auth', 'session_token');
  
  if (!tokenEntry || !tokenEntry.value) {
    throw new Error('Nicht eingeloggt');
  }

  // Token-Ablauf prüfen
  if (tokenEntry.expiresAt) {
    const expiresAt = new Date(tokenEntry.expiresAt);
    if (expiresAt < new Date()) {
      throw new Error('Session abgelaufen');
    }
  }

  return tokenEntry.value;
}

/**
 * Gibt Sync-Status zurück
 */
export async function getSyncStatus() {
  const db = await openDB(DB_NAME, DB_VERSION);

  const [lastFullSync, lastDeltaSync, syncInProgress, totalParts] = await Promise.all([
    db.get('sync_metadata', 'last_full_sync'),
    db.get('sync_metadata', 'last_delta_sync'),
    db.get('sync_metadata', 'sync_in_progress'),
    db.get('sync_metadata', 'total_parts_count')
  ]);

  return {
    lastFullSync: lastFullSync?.value || null,
    lastDeltaSync: lastDeltaSync?.value || null,
    syncInProgress: syncInProgress?.value || false,
    totalParts: totalParts?.value || 0,
    isOnline: navigator.onLine,
    networkInfo: isSuitableForSync()
  };
}

// ============================================================
// AUTO-SYNC - Automatische Hintergrund-Synchronisation
// ============================================================

/**
 * Startet automatische Hintergrund-Synchronisation
 * - Prüft beim Start ob Daten älter als 24 Stunden
 * - Lädt Daten und Bilder automatisch herunter
 * - Läuft komplett im Hintergrund ohne UI-Blocking
 */
// ============================================================
// LOGIN FULL SYNC - Alle Daten beim Login herunterladen
// ============================================================

/**
 * Lädt einen Teil von /sync/full-data. Mit etagKey wird If-None-Match geschickt:
 * Hat sich nichts geändert, antwortet der Server mit 304 und es wird nichts übertragen.
 * @returns {Promise<object|null>} Daten oder null bei 304
 */
async function fetchSyncSections(db, token, sections, etagKey = null) {
  const headers = { 'Authorization': `Bearer ${token}` };
  const stored = etagKey ? (await db.get('sync_metadata', etagKey))?.value : null;
  if (stored) headers['If-None-Match'] = stored;

  const response = await fetch(`${API_BASE_URL}/sync/full-data?sections=${sections.join(',')}`, {
    headers,
    cache: 'no-store'
  });
  if (response.status === 304) return null;
  if (!response.ok) throw new Error(`Server-Fehler: ${response.status}`);

  const data = await response.json();
  if (etagKey) {
    const etag = response.headers.get('ETag');
    // erst nach dem Speichern merken – sonst fehlen die Daten bei Abbruch dauerhaft
    data.__commitEtag = () => db.put('sync_metadata', { key: etagKey, value: etag, updatedAt: new Date().toISOString() });
  }
  return data;
}

// Ein hängengebliebenes "Sync läuft" (App während Sync geschlossen) blockiert nicht ewig.
const SYNC_LOCK_STALE_MS = 3 * 60 * 1000;

/**
 * Lädt beim Login alle Daten vom Server und speichert sie in IndexedDB.
 * Läuft im Hintergrund nach dem Login. Ersatzteile/Doku/News nur im WLAN (siehe canPrefetchNow),
 * Doku/News zudem nur, wenn sie sich seit dem letzten Abruf geändert haben.
 * Binäre Dateien (Bilder, PDFs) werden im Hintergrund nachgeladen.
 *
 * @param {Function} onProgress - Callback(percent, message)
 * @param {{manual?: boolean}} options - manual: vom Nutzer angestoßen → im WLAN auch bei "Nie automatisch"
 * @returns {Promise<{success: boolean, counts: object, error?: string}>}
 */
export async function loginFullSync(onProgress = null, { manual = false } = {}) {
  if (!navigator.onLine) {
    console.log('[LoginSync] Offline – skipping sync');
    return { success: false, reason: 'offline' };
  }

  const db = await openDB(DB_NAME, DB_VERSION);

  // Prüfe ob bereits ein Sync läuft
  const syncInProgress = await db.get('sync_metadata', 'sync_in_progress');
  if (syncInProgress?.value && Date.now() - new Date(syncInProgress.updatedAt).getTime() < SYNC_LOCK_STALE_MS) {
    console.log('[LoginSync] Sync already running');
    return { success: false, reason: 'already_running' };
  }

  try {
    await db.put('sync_metadata', { key: 'sync_in_progress', value: true, updatedAt: new Date().toISOString() });

    if (onProgress) onProgress(5, 'Verbindung wird hergestellt...');

    // Große Daten (Ersatzteile, Doku, News) nur im WLAN bzw. laut Einstellung "Offline-Daten laden".
    // Kleine Daten (Kunden, Easyhelp, Passwörter) immer.
    const bulkAllowed = manual ? canLoadRemotePartImages() : canPrefetchNow();
    const token = await getSessionToken();

    const [base, docData, newsData] = await Promise.all([
      fetchSyncSections(db, token, ['customers', 'easyhelp', 'passwords', ...(bulkAllowed ? ['catalog'] : [])]),
      bulkAllowed ? fetchSyncSections(db, token, ['documentation'], 'etag_documentation') : null,
      bulkAllowed ? fetchSyncSections(db, token, ['news'], 'etag_news') : null
    ]);
    const data = { ...base, documentation: docData?.documentation, news: newsData?.news };
    if (onProgress) onProgress(20, 'Daten empfangen – speichern...');

    if (data.categories) {
      const catTx = db.transaction('categories', 'readwrite');
      await catTx.objectStore('categories').clear();
      for (const cat of data.categories) await catTx.objectStore('categories').put(cat);
      await catTx.done;

      const devTx = db.transaction('devices', 'readwrite');
      await devTx.objectStore('devices').clear();
      for (const dev of (data.devices || [])) await devTx.objectStore('devices').put(dev);
      await devTx.done;
      if (onProgress) onProgress(40, `${(data.devices || []).length} Geräte geladen`);
    }

    // Kunden
    await saveCustomers(data.customers || []);
    if (onProgress) onProgress(45, `${(data.customers || []).length} Kunden geladen`);

    // Ersatzteile
    if (data.parts) {
      const partsTx = db.transaction('parts', 'readwrite');
      await partsTx.objectStore('parts').clear();
      for (const part of data.parts) await partsTx.objectStore('parts').put(part);
      await partsTx.done;
      if (onProgress) onProgress(55, `${data.parts.length} Ersatzteile geladen`);
    }

    // Dokumentationen (nur wenn geändert)
    if (data.documentation) {
      await saveDocumentationForOffline(data.documentation.docs || [], data.documentation.attachments || []);
      await docData.__commitEtag?.();
      if (onProgress) onProgress(65, `${(data.documentation.docs || []).length} Dokumentationen geladen`);
    }

    // News Feeds (nur wenn geändert)
    if (data.news) {
      await saveNewsFeeds(data.news.feeds || []);
      await saveNewsFeedAttachments(data.news.attachments || []);
      await newsData.__commitEtag?.();
      if (onProgress) onProgress(75, `${(data.news.feeds || []).length} News-Feeds geladen`);
    }

    // Easyhelp Cases + Messages
    await saveEasyhelpCases(data.easyhelp?.cases || []);
    await saveEasyhelpMessages(data.easyhelp?.messages || []);
    if (onProgress) onProgress(85, `${(data.easyhelp?.cases || []).length} Easyhelp-Fälle geladen`);

    // Passwords
    if (data.passwords) {
      await savePasswords(data.passwords);
      if (onProgress) onProgress(88, `${data.passwords.length} Passwörter geladen`);
    }

    // Sync-Timestamps setzen (Ersatzteil-Stand nur, wenn er auch geladen wurde)
    const now = new Date().toISOString();
    if (data.parts) {
      await db.put('sync_metadata', { key: 'last_full_sync', value: now, updatedAt: now });
      await db.put('sync_metadata', { key: 'last_delta_sync', value: now, updatedAt: now });
      await db.put('sync_metadata', { key: 'total_parts_count', value: data.parts.length, updatedAt: now });
    }

    if (onProgress) onProgress(90, 'Bilder werden im Hintergrund geladen...');

    const counts = {
      categories: (data.categories || []).length,
      devices: (data.devices || []).length,
      customers: (data.customers || []).length,
      parts: (data.parts || []).length,
      docs: (data.documentation?.docs || []).length,
      newsFeeds: (data.news?.feeds || []).length,
      easyhelpCases: (data.easyhelp?.cases || []).length,
      easyhelpMessages: (data.easyhelp?.messages || []).length,
      bulkSkipped: !bulkAllowed,
      docsUnchanged: bulkAllowed && !docData,
      newsUnchanged: bulkAllowed && !newsData,
    };

    console.log('[LoginSync] Metadata sync complete:', counts);

    // Binäre Dateien im Hintergrund laden (blockiert nicht)
    _downloadBinaryFilesInBackground(data, token);

    if (onProgress) onProgress(100, 'Synchronisation abgeschlossen');

    window.dispatchEvent(new CustomEvent('sync-completed', { detail: { type: 'login-full', counts } }));

    return { success: true, counts };

  } catch (error) {
    console.error('[LoginSync] Failed:', error);
    if (onProgress) onProgress(-1, `Fehler: ${error.message}`);
    return { success: false, error: error.message };
  } finally {
    await db.put('sync_metadata', { key: 'sync_in_progress', value: false, updatedAt: new Date().toISOString() });
  }
}

/**
 * Lädt Bilder/Dateien im Hintergrund (non-blocking).
 * - Part-Bilder
 * - Device-Bilder
 * - Doc-Anhänge (PDF/Bild)
 * - Easyhelp Chat-Dateien (Bild/PDF)
 */
async function _downloadBinaryFilesInBackground(data, token) {
  if (IS_DEV) return;

  try {
    const db = await openDB(DB_NAME, DB_VERSION);
    const BATCH = 3;
    const DELAY = 800;

    // Helper: download blob and store
    async function downloadBlob(url, store, key) {
      try {
        const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (!r.ok) return;
        const blob = await r.blob();
        await store(key, blob);
      } catch { /* silently ignore */ }
    }

    // Teile-, Geräte- und Kundenbilder: nur fehlende bzw. ersetzte (Version = Dateiname)
    const imageResult = await prefetchCatalogImages(
      { parts: data.parts || [], devices: data.devices || [], customers: data.customers || [] },
      API_BASE_URL,
      token
    );
    console.log('[LoginSync] Bilder:', imageResult);

    // Große Anhänge (Doku, Easyhelp, News) nur laden, wenn Vorladen gerade erlaubt ist (WLAN/Einstellung)
    if (!canPrefetchNow()) {
      console.log('[LoginSync] Anhänge werden nicht vorgeladen (Einstellung/Mobilfunk/Datensparmodus)');
      return;
    }

    // Doc attachment files (PDF + images only)
    const viewableAttachments = (data.documentation?.attachments || []).filter(
      a => a.mimetype === 'application/pdf' || (a.mimetype && a.mimetype.startsWith('image/'))
    );
    for (let i = 0; i < viewableAttachments.length; i += BATCH) {
      const batch = viewableAttachments.slice(i, i + BATCH);
      await Promise.all(batch.map(async att => {
        const cached = await db.get('doc_attachment_files', att.id);
        if (cached) return;
        await downloadBlob(
          `${API_BASE_URL}/documentation/attachments/${att.id}/file`,
          (key, blob) => saveDocAttachmentFile(key, blob),
          att.id
        );
      }));
      if (i + BATCH < viewableAttachments.length) await new Promise(r => setTimeout(r, DELAY));
    }
    console.log(`[LoginSync] Doc attachments: ${viewableAttachments.length} checked`);

    // Easyhelp file messages (images + PDFs in chat)
    // NOTE: The file is served by its stored filename (file_path), the original
    // display name (file_name) is NOT a valid URL/key.
    const ehFileMessages = (data.easyhelp?.messages || []).filter(
      m => m.file_path && (
        m.file_mimetype === 'application/pdf' || (m.file_mimetype && m.file_mimetype.startsWith('image/'))
      )
    );
    for (let i = 0; i < ehFileMessages.length; i += BATCH) {
      const batch = ehFileMessages.slice(i, i + BATCH);
      await Promise.all(batch.map(async msg => {
        const cached = await getEasyhelpFileCache(msg.file_path);
        if (cached) return;
        const r = await fetch(`${API_BASE_URL}/easyhelp/uploads/${encodeURIComponent(msg.file_path)}?token=${token}`);
        if (!r.ok) return;
        const blob = await r.blob();
        await saveEasyhelpFileCache(msg.file_path, blob);
      }));
      if (i + BATCH < ehFileMessages.length) await new Promise(r => setTimeout(r, DELAY));
    }
    console.log(`[LoginSync] Easyhelp files: ${ehFileMessages.length} checked`);

    // News feed attachment files (images + PDFs)
    const newsViewableAttachments = (data.news?.attachments || []).filter(
      a => a.mimetype === 'application/pdf' || (a.mimetype && a.mimetype.startsWith('image/'))
    );
    for (let i = 0; i < newsViewableAttachments.length; i += BATCH) {
      const batch = newsViewableAttachments.slice(i, i + BATCH);
      await Promise.all(batch.map(async att => {
        const cached = await getNewsFeedAttachmentFileOffline(att.id);
        if (cached) return;
        await downloadBlob(
          `${API_BASE_URL}/news/attachments/${att.id}/file`,
          (key, blob) => saveNewsFeedAttachmentFile(key, blob),
          att.id
        );
      }));
      if (i + BATCH < newsViewableAttachments.length) await new Promise(r => setTimeout(r, DELAY));
    }
    console.log(`[LoginSync] News attachments: ${newsViewableAttachments.length} checked`);

  } catch (error) {
    console.warn('[LoginSync] Background binary download error:', error);
  }
}

// ============================================================
// DOCUMENTATION OFFLINE SYNC
// ============================================================

/**
 * Fetches all documentation metadata + attachment metadata, stores in IndexedDB.
 * Then downloads attachment file blobs (PDFs, images) in small background batches.
 */
export async function syncDocumentationForOffline() {
  if (!canPrefetchNow()) {
    console.log('[DocSync] Offline/Mobilfunk – skipping documentation sync');
    return;
  }

  try {
    const token = await getSessionToken();
    const db = await openDB(DB_NAME, DB_VERSION);
    // Nur bei Änderungen übertragen (ETag/304) – die Doku ist mit eingebetteten Bildern sehr groß
    const data = await fetchSyncSections(db, token, ['documentation'], 'etag_documentation');
    if (!data) {
      console.log('[DocSync] Unverändert (304)');
      return;
    }

    const { docs, attachments } = data.documentation;
    await saveDocumentationForOffline(docs, attachments);
    await data.__commitEtag?.();

    console.log(`[DocSync] Stored ${docs.length} docs and ${attachments.length} attachments in IndexedDB`);

    // Download viewable attachments (PDF + images) in the background
    const viewable = attachments.filter(
      a => a.mimetype === 'application/pdf' || a.mimetype.startsWith('image/')
    );

    if (viewable.length > 0) {
      console.log(`[DocSync] Downloading ${viewable.length} viewable attachment files in background...`);
      downloadDocAttachmentFiles(viewable, token);
    }

    // Update sync timestamp
    await db.put('sync_metadata', {
      key: 'last_doc_sync',
      value: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

  } catch (error) {
    console.error('[DocSync] Documentation sync failed:', error);
  }
}

/**
 * Downloads attachment file blobs in small batches and stores them in IndexedDB.
 * Only downloads PDFs and images (things that can be previewed offline).
 * Skips files already cached.
 */
async function downloadDocAttachmentFiles(attachments, token) {
  // Große Dateien nur vorladen, wenn es die Einstellung/Verbindung erlaubt (sonst beim Öffnen)
  if (!canPrefetchNow()) return 0;
  const db = await openDB(DB_NAME, DB_VERSION);
  const BATCH_SIZE = 3;
  const BATCH_DELAY_MS = 600;
  let downloaded = 0;

  for (let i = 0; i < attachments.length; i += BATCH_SIZE) {
    const batch = attachments.slice(i, i + BATCH_SIZE);

    await Promise.all(batch.map(async (att) => {
      try {
        // Skip if already cached (no expiry for docs – admin can force re-sync)
        const cached = await db.get('doc_attachment_files', att.id);
        if (cached) return;

        const r = await fetch(`${API_BASE_URL}/documentation/attachments/${att.id}/file`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) return;

        const blob = await r.blob();
        await saveDocAttachmentFile(att.id, blob);
        downloaded++;
        console.log(`[DocSync] ✅ Cached attachment ${att.id} (${att.original_name})`);
      } catch (err) {
        console.warn(`[DocSync] Failed to cache attachment ${att.id}:`, err);
      }
    }));

    if (i + BATCH_SIZE < attachments.length) {
      await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
    }
  }

  console.log(`[DocSync] Attachment file download complete: ${downloaded}/${attachments.length}`);
}

export async function startAutoSync() {
  console.log('[AutoSync] Starting background synchronization...');

  // Zurücksetzen des sync_in_progress Flags, falls der Browser während eines
  // laufenden Syncs geschlossen wurde und das Flag in IndexedDB hängengeblieben ist.
  try {
    const db = await openDB(DB_NAME, DB_VERSION);
    await db.put('sync_metadata', {
      key: 'sync_in_progress',
      value: false,
      updatedAt: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[AutoSync] Could not reset sync_in_progress flag:', e);
  }
  
  // Warte kurz damit die App zuerst laden kann
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  try {
    const shouldSync = await shouldPerformAutoSync();
    
    if (shouldSync.needed) {
      console.log(`[AutoSync] Sync needed: ${shouldSync.reason}`);
      await performBackgroundSync();
    } else {
      console.log(`[AutoSync] No sync needed: ${shouldSync.reason}`);
    }
    
    // Bilder im Hintergrund laden (falls noch fehlend)
    await downloadMissingImagesInBackground();

    // Dokumentationen für Offline synchronisieren
    await syncDocumentationForOffline();
    
  } catch (error) {
    console.error('[AutoSync] Failed:', error);
  }
  
  // Periodische Prüfung alle 30 Minuten
  setInterval(async () => {
    try {
      const shouldSync = await shouldPerformAutoSync();
      if (shouldSync.needed) {
        console.log(`[AutoSync] Periodic check: Sync needed - ${shouldSync.reason}`);
        await performBackgroundSync();
      }
    } catch (error) {
      console.error('[AutoSync] Periodic check failed:', error);
    }
  }, 30 * 60 * 1000); // 30 Minuten
}

/**
 * Prüft ob Auto-Sync durchgeführt werden soll
 * - Daten älter als 24 Stunden
 * - Noch nie synchronisiert
 */
async function shouldPerformAutoSync() {
  try {
    // Offline? Kein Sync möglich
    if (!navigator.onLine) {
      return { needed: false, reason: 'Offline' };
    }
    
    const db = await openDB(DB_NAME, DB_VERSION);
    
    // Läuft bereits ein Sync?
    const syncInProgress = await db.get('sync_metadata', 'sync_in_progress');
    if (syncInProgress?.value) {
      return { needed: false, reason: 'Sync läuft bereits' };
    }
    
    // Letzter Sync
    const lastFullSync = await db.get('sync_metadata', 'last_full_sync');
    const lastDeltaSync = await db.get('sync_metadata', 'last_delta_sync');
    
    const lastSyncTime = lastDeltaSync?.value || lastFullSync?.value;
    
    // Noch nie synchronisiert?
    if (!lastSyncTime) {
      return { needed: true, reason: 'Noch nie synchronisiert' };
    }
    
    // Älter als 24 Stunden?
    const hoursSinceSync = (Date.now() - new Date(lastSyncTime).getTime()) / (1000 * 60 * 60);
    
    if (hoursSinceSync >= 24) {
      return { 
        needed: true, 
        reason: `Daten ${Math.floor(hoursSinceSync)} Stunden alt` 
      };
    }
    
    return { 
      needed: false, 
      reason: `Letzter Sync vor ${Math.floor(hoursSinceSync)} Stunden` 
    };
    
  } catch (error) {
    console.error('[AutoSync] Check failed:', error);
    return { needed: false, reason: 'Fehler bei Prüfung' };
  }
}

/**
 * Führt Hintergrund-Synchronisation durch
 * - Nutzt Delta-Update wenn möglich
 * - Läuft komplett im Hintergrund
 * - Keine UI-Blockierung
 */
async function performBackgroundSync() {
  try {
    console.log('[AutoSync] Starting background data sync...');
    
    // Network-Check
    const networkCheck = isSuitableForSync();
    
    // Bei schlechter Verbindung: Verschieben
    if (!networkCheck.allowed) {
      console.log('[AutoSync] Network nicht geeignet, verschiebe Sync');
      return;
    }
    
    // Delta-Update versuchen
    const result = await performDeltaUpdate(networkCheck.limitSize);
    
    if (result.success) {
      console.log('[AutoSync] Data sync completed successfully');
      console.log(`[AutoSync] Added: ${result.addedCount}, Updated: ${result.updatedCount}, Deleted: ${result.deletedCount}`);
      // Event wird bereits in performDeltaUpdate ausgelöst
    } else {
      console.warn('[AutoSync] Data sync failed:', result.reason);
    }
    
  } catch (error) {
    console.error('[AutoSync] Background sync failed:', error);
  }
}

/**
 * Lädt fehlende Bilder im Hintergrund herunter
 * - Prüft welche Bilder fehlen
 * - Lädt sie in kleinen Batches
 * - Keine UI-Blockierung
 */
export async function downloadMissingImagesInBackground({ force = false } = {}) {
  // Lädt nur Bilder, die noch nicht im Offline-Cache liegen oder ersetzt wurden.
  // Kein pauschales Neuladen nach X Tagen mehr – das hat früher jede Woche alle Bilder erneut geladen.
  try {
    if (!navigator.onLine || IS_DEV) return null;
    const token = await getSessionToken();
    if (!token) return null;
    const db = await openDB(DB_NAME, DB_VERSION);
    const [parts, devices, customers] = await Promise.all([
      db.getAll('parts'),
      db.getAll('devices'),
      db.objectStoreNames.contains('customers') ? db.getAll('customers') : []
    ]);
    const result = await prefetchCatalogImages({ parts, devices, customers }, API_BASE_URL, token, { force });
    console.log('[AutoSync] Bilder:', result);

    // Alter Bildspeicher in IndexedDB wird nicht mehr gebraucht, sobald alles im Cache liegt
    if (!result.skipped && result.loaded === result.missing) {
      for (const store of ['part_images', 'device_images']) {
        if (db.objectStoreNames.contains(store)) await db.clear(store);
      }
    }
    return result;
  } catch (error) {
    console.error('[AutoSync] Image download failed:', error);
    return null;
  }
}
